'use client';

import React from 'react';
import { Box, Stack, Drawer, useMediaQuery, Button, Typography, StateCard } from '@unity/cloud-ui';
import { styled } from '@unity/cloud-ui/styles';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowElbowLeftUp, MagnifyingGlass } from '@phosphor-icons/react';
import NextLink from 'next/link';
import { SidebarDataType, SidebarItem } from '@/lib/content-loader/types';

import { manifestSchema } from '@/lib/content-loader/schemas/manifest.schema';
import { normalizeInternalPath } from '@/utils/normalize-internal-path';
import themeConstants from '../theme/theme-constants';
import { SidebarTreeView } from './components/sidebar-tree-view';
import { SidebarContext } from './sidebar-provider';
import { AvailableVersions, VersionPicker } from './components/version-picker';
import { SidebarFilter } from './components/sidebar-filter';
import type { SidebarTreeItem } from './types';

const SidebarDrawer = styled(Drawer)(({ theme }) => ({
  width: '75vw',
  flexShrink: 0,
  maxWidth: theme.layout.sidebarDrawerMaxWidth,
  [`& .MuiDrawer-paper`]: {
    width: '75vw',
    maxWidth: theme.layout.sidebarDrawerMaxWidth,
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
  },
  [`& .MuiDrawer-paper > div`]: {
    padding: 0,
  },
}));

type ParentSidebar = manifestSchema[string]['sidebars'][number];

export function Sidebar(props: {
  content: SidebarDataType | null;
  parentSidebar: ParentSidebar | null;
  versions: AvailableVersions;
}) {
  const t = useTranslations();
  const {
    sidebarOpen,
    setSidebarState,
    searchResults,
    setSearchResults,
    filteredSections,
    setFilteredSections,
    searchValue,
    setSearchValue,
    sectionsHashKey,
    setSectionsHashKey,
    setScrollPosition,
    scrollPosition,
    expandedItems,
    setExpandedItems,
  } = React.useContext(SidebarContext);

  const boxRef = React.useRef<HTMLDivElement | undefined>(undefined);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.between('xs', 'md'));
  const locale = useLocale();
  const localePathRegex = new RegExp(`^/${locale}`, 'i');
  const normalizedPathname = normalizeInternalPath(usePathname().replace(localePathRegex, ''));

  React.useEffect(() => {
    // reset the current scrollTop position after
    // a sidebar navigation
    if (boxRef.current && scrollPosition) {
      const { overflowY = 'auto' } = window.getComputedStyle(boxRef.current);
      boxRef.current.style.overflowY = 'hidden';
      boxRef.current.scroll({ top: scrollPosition, behavior: 'instant' });
      boxRef.current.style.overflowY = overflowY;
    }
  }, [scrollPosition]);

  const { parsedSidebarItems, focusedItem, defaultExpandedItems, allItems } = React.useMemo(
    () =>
      props.content
        ? transformDataToTreeView(props.content, normalizedPathname)
        : { parsedSidebarItems: null, focusedItem: '', defaultExpandedItems: [], allItems: [] },
    [props.content, normalizedPathname],
  );
  const parsedSidebarItemsHashKey = parsedSidebarItems?.reduce((acc, section) => {
    return `${acc},${section.title}`;
  }, '');

  const [selectedItem, setSelectedItem] = React.useState<string | null>(focusedItem);

  React.useEffect(() => {
    // clear search on subnav click
    if (parsedSidebarItemsHashKey !== sectionsHashKey) {
      setSectionsHashKey(parsedSidebarItemsHashKey ?? '');
      setSearchValue('');
      setSearchResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedSidebarItemsHashKey]);

  React.useEffect(() => {
    if (parsedSidebarItems) {
      setFilteredSections(parsedSidebarItems);
    }
  }, [setFilteredSections, parsedSidebarItems]);

  React.useEffect(() => {
    setExpandedItems([...expandedItems, ...defaultExpandedItems]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setExpandedItems, defaultExpandedItems]);

  if (!parsedSidebarItems) {
    return null;
  }

  function handleItemClick() {
    setSidebarState(false);
    setSelectedItem(null);

    if (boxRef.current) {
      const scrollPos =
        (boxRef.current?.getBoundingClientRect().top || 0) -
        (boxRef.current?.firstElementChild?.getBoundingClientRect().top || 0);
      setScrollPosition(scrollPos);
    }
  }

  const sectionsToShow = filteredSections || parsedSidebarItems;

  const renderSidebarContent = () => (
    <Stack spacing={2} sx={{ width: '100%', p: 2 }}>
      {props.parentSidebar && (
        <>
          <Button
            fullWidth
            color="default"
            startIcon={<ArrowElbowLeftUp size={18} />}
            component={NextLink}
            href={props.parentSidebar.path}
            aria-label={t('accessibility:navitate-back-to-item', { item: props.parentSidebar.title })}
            sx={{ justifyContent: 'flex-start' }}
          >
            <Typography noWrap component="span" variant="body2" fontWeight={500}>
              {props.parentSidebar.title}
            </Typography>
          </Button>
        </>
      )}
      {props.versions?.length > 0 && <VersionPicker versions={props.versions} />}
      <SidebarFilter initialData={parsedSidebarItems} />
      {sectionsToShow.length === 0 && <StateCard title={t('sidebar:no-search-results')} icon={MagnifyingGlass} />}
      {sectionsToShow.map((section) => (
        <SidebarTreeView
          key={section.title}
          section={section}
          searchResults={searchResults}
          selectedItem={selectedItem}
          expandedItems={searchValue ? allItems : expandedItems}
          setExpandedItems={setExpandedItems}
          onItemClick={handleItemClick}
        />
      ))}
    </Stack>
  );

  if (isMobile) {
    return (
      <SidebarDrawer anchor="left" open={sidebarOpen} onClose={() => setSidebarState(false)}>
        {renderSidebarContent()}
      </SidebarDrawer>
    );
  }

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'fixed',
        height: `calc(100svh - ${themeConstants.layout.headerHeight} - ${themeConstants.layout.productHeaderHeight})`,
        width: 'inherit',
        overflowY: 'auto',
        backgroundColor: 'background.paper',
        maxWidth: themeConstants.layout.sidebarDrawerMaxWidth,
        borderRight: (theme) => `1px solid ${theme.vars.palette.divider}`,
      }}
    >
      {renderSidebarContent()}
    </Box>
  );
}

// Function to transform sidebar data into a tree view structure
function transformDataToTreeView(
  data: SidebarDataType,
  pathname: string,
): {
  parsedSidebarItems: { title: string; items: SidebarTreeItem[] }[];
  focusedItem: string;
  defaultExpandedItems: string[];
  allItems: string[];
} {
  let focusedItem = '';
  let selectedParentId = '';
  const defaultExpandedItems: string[] = [];
  const allItems: string[] = [];

  // Recursive function to map items to tree view structure
  const mapItems = (items: SidebarItem[], parentId = ''): SidebarTreeItem[] => {
    return items.map((item, index) => {
      const nodeId = parentId ? `${parentId}${item.title}-${index}` : `${item.title}-${index}`;
      let children: SidebarTreeItem[] = [];

      allItems.push(nodeId);

      // Check if the current item is the selected item based on the pathname
      if (pathname === normalizeInternalPath(item.href)) {
        focusedItem = nodeId;
        defaultExpandedItems.push(nodeId);
        selectedParentId = parentId;
      }

      if (item.items && item.items.length > 0) {
        children = mapItems(item.items, nodeId);

        // Ensure parent items of focusedItem are expanded
        if (selectedParentId === nodeId) {
          defaultExpandedItems.push(nodeId);
          selectedParentId = parentId;
        }
      }

      return {
        id: nodeId,
        href: item.href,
        label: item.title,
        children,
      };
    });
  };

  const parsedSidebarItems =
    data.sections?.map((section) => ({
      title: section.title,
      items: mapItems(section.items),
    })) ?? [];

  return { parsedSidebarItems, focusedItem, defaultExpandedItems, allItems };
}
